'use client';

import cn from 'classnames';
import React, { forwardRef, useRef, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react';
import { mergeRefs } from 'react-merge-refs';
import Link from 'next/link';

import LoadingDots from '@/components/ui/LoadingDots';
import { TrackingEvent } from '@/utils/helpers';
import styles from './Button.module.css';
import { analytics } from '@/lib/analytics';

type ButtonOrAnchorProps = ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

interface BaseProps {
  variant?: 'slim' | 'flat';
  active?: boolean;
  width?: number;
  loading?: boolean;
  isPrimary?: boolean;
  isSecondary?: boolean;
  /** Destructive confirmations. Like `isPrimary`, it yields to the disabled
   * and loading styles, so the button only turns red once it can actually be
   * pressed. */
  isWarning?: boolean;
  tracking?: TrackingEvent;
}

type ButtonProps = BaseProps &
  ButtonOrAnchorProps & {
    href?: never;
  };

type AnchorProps = BaseProps &
  ButtonOrAnchorProps & {
    href: string;
  };

type Props = ButtonProps | AnchorProps;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>((props, forwardedRef) => {
  const {
    className,
    variant = 'flat',
    children,
    active,
    width,
    loading = false,
    disabled = false,
    style = {},
    isPrimary = false,
    isSecondary = false,
    isWarning = false,
    href,
    onClick = () => {},
    tracking,
    ...rest
  } = props;

  const innerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const rootClassName = cn(
    styles.root,
    {
      [styles.slim]: variant === 'slim',
      [styles.primary]: isPrimary && !disabled && !loading,
      [styles.secondary]: isSecondary && !disabled && !loading,
      [styles.warning]: isWarning && !disabled && !loading,
      [styles.loading]: loading,
      [styles.disabled]: disabled,
    },
    className
  );

  const Component = href ? 'a' : 'button';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (tracking) {
      analytics.capture(tracking.event, tracking.properties);
    }
    onClick(e as React.MouseEvent<HTMLButtonElement>);
  };

  return (
    <Component
      aria-pressed={active}
      data-variant={variant}
      ref={mergeRefs([innerRef, forwardedRef])}
      className={rootClassName}
      disabled={disabled}
      style={{
        width,
        ...style,
        ...{ textDecoration: 'none' },
      }}
      onClick={handleClick}
      href={href}
      {...rest}
    >
      {children}
      {loading && (
        <i className="flex pl-2 m-0">
          <LoadingDots />
        </i>
      )}
    </Component>
  );
});
Button.displayName = 'Button';

interface LinkButtonProps extends Omit<AnchorProps, 'href'> {
  href: string;
}

const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>((props, ref) => {
  const { href, ...rest } = props;
  return (
    <Link href={href} passHref legacyBehavior>
      <Button ref={ref} href={href} {...rest} />
    </Link>
  );
});
LinkButton.displayName = 'LinkButton';

export { Button, LinkButton };
export default Button;
